import React, { useRef } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { media, spacing } from '../../theme';
import colors from '../../theme/variables/colors';

interface TooltipPosition {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
}

interface TooltipContainerAttr {
    $showTooltip: boolean;
    $position?: TooltipPosition;
}

const TooltipContainer = styled.div<TooltipContainerAttr>`
    .tooltip {
        position: fixed;
        visibility: ${props => (props?.$showTooltip ? 'visible' : 'hidden')};
        width: auto;
        ${props => `
            top: ${props.$position?.top ? `${props.$position?.top}px` : ''};
            right: ${props.$position?.right ? `${props.$position?.right}px` : ''};
            bottom: ${props.$position?.bottom ? `${props.$position?.bottom}px` : ''};
            left: ${props.$position?.left ? `${props.$position?.left}px` : ''};
        `}

        .tooltip-content {
            background-color: ${colors.palette.black};
            padding: ${spacing.spacing2} ${spacing.spacing4};
            color: ${colors.palette.white};
            z-index: 1;
            border-radius: 3px;
            opacity: 0.9;
            transition: opacity 0.15s ease-out;
            width: 100%;
            max-width: 300px;

            @media ${media.small} {
                max-width: 500px;
            }
        }
    }
`;

const ChildrenContainer = styled.div`
    display: inline-flex;
`;

export interface TooltipProps {
    children: React.ReactNode;
    content: string | React.ReactNode;
    className?: string;
}

const Tooltip = ({ children, content, className }: TooltipProps) => {
    const tooltipEl = useRef<HTMLDivElement>(null);
    const isHovering = useRef(false);
    const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>();
    const [showTooltip, setShowTooltip] = useState(false);

    const handleOnMouseEnter = (e: React.MouseEvent) => {
        isHovering.current = true;
        const cursorX = e.clientX;
        const cursorY = e.clientY;
        const tooltipBoundRect: DOMRect | undefined = tooltipEl.current?.getBoundingClientRect();
        const tooltipWidth = tooltipBoundRect?.width || 0;
        const tooltipHeight = tooltipBoundRect?.height || 0;
        const offset = 3;

        let top = cursorY + offset;
        let left: number | undefined = cursorX + offset;
        let right: number | undefined = 0;

        if (left + tooltipWidth > window.innerWidth) {
            right = offset;
            left = undefined;
        }

        if (top + tooltipHeight > window.innerHeight) {
            top = cursorY - tooltipHeight - offset;
        }

        setTooltipPosition({
            top: top || undefined,
            right: right || undefined,
            left: left || undefined,
        });

        // to avoid screen flickering set show tooltip after a delay
        setTimeout(() => {
            if (isHovering.current) {
                setShowTooltip(true);
            }
        }, 1000);
    };

    const handleOnMouseLeave = () => {
        isHovering.current = false;
        setShowTooltip(false);
    };

    return (
        <TooltipContainer
            className={className}
            $showTooltip={showTooltip}
            $position={tooltipPosition}
        >
            <ChildrenContainer
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
            >
                {children}
            </ChildrenContainer>
            <div className="tooltip" ref={tooltipEl}>
                <div className="tooltip-content">{content}</div>
            </div>
        </TooltipContainer>
    );
};

export default Tooltip;
